import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Plus, Pencil, Trash2, Mail, Phone, Building2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useWorkspace } from '@/context/WorkspaceContext';
import { canEditEntry } from '@/lib/permissions';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = {
    first_name: '', last_name: '', role: '', organization: '',
    email: '', phone: '', notes: '', position: 0,
};

export default function ProjectActors({ projectId }) {
    const { profile, session, user } = useAuth();
    const { currentWorkspace } = useWorkspace();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(empty);
    const [submitting, setSubmitting] = useState(false);

    const isAdmin = !!currentWorkspace?.is_admin || profile?.is_super_admin;

    const authHeader = useCallback(() => ({
        headers: { Authorization: `Bearer ${session?.access_token}` },
    }), [session]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await axios.get(`${API}/projects/${projectId}/actors`, authHeader());
            setItems(r.data.items || []);
        } catch (e) {
            toast.error('Chargement des acteurs échoué');
        } finally {
            setLoading(false);
        }
    }, [projectId, authHeader]);

    useEffect(() => { load(); }, [load]);

    const openCreate = () => {
        setEditingId(null);
        setForm({ ...empty, position: items.length });
        setOpen(true);
    };
    const openEdit = (a) => {
        setEditingId(a.id);
        setForm({
            first_name: a.first_name || '', last_name: a.last_name || '',
            role: a.role || '', organization: a.organization || '',
            email: a.email || '', phone: a.phone || '',
            notes: a.notes || '', position: a.position ?? 0,
        });
        setOpen(true);
    };

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const body = { ...form };
            if (!body.email) delete body.email;
            if (editingId) {
                await axios.patch(`${API}/projects/${projectId}/actors/${editingId}`, body, authHeader());
                toast.success('Acteur modifié');
            } else {
                await axios.post(`${API}/projects/${projectId}/actors`, body, authHeader());
                toast.success('Acteur ajouté');
            }
            setOpen(false);
            load();
        } catch (e2) {
            toast.error(e2?.response?.data?.detail || 'Échec');
        } finally {
            setSubmitting(false);
        }
    };

    const remove = async (a) => {
        if (!window.confirm(`Supprimer ${a.first_name || ''} ${a.last_name || ''} ?`)) return;
        try {
            await axios.delete(`${API}/projects/${projectId}/actors/${a.id}`, authHeader());
            toast.success('Acteur supprimé');
            load();
        } catch (e2) {
            toast.error('Suppression refusée');
        }
    };

    if (loading) return <div className="py-10 text-center text-muted-foreground"><Loader2 className="inline animate-spin mr-2" size={14} /> Chargement…</div>;

    return (
        <div className="space-y-5" data-testid="project-actors">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h3 className="font-heading text-xl">Acteurs du projet</h3>
                    <p className="text-sm text-muted-foreground mt-0.5">
                        Sponsors, équipe projet, partenaires et contacts clés.
                    </p>
                </div>
                {isAdmin && (
                    <Button onClick={openCreate} size="sm" className="bg-primary hover:bg-primary-hover" data-testid="actor-add-btn">
                        <Plus size={14} className="mr-1.5" /> Acteur
                    </Button>
                )}
            </div>

            {items.length === 0 ? (
                <div className="text-center py-12 border border-dashed border-border rounded-xl bg-surface-muted/30">
                    <p className="text-sm text-muted-foreground">Aucun acteur référencé pour ce projet.</p>
                    {isAdmin && (
                        <button onClick={openCreate} className="text-xs text-primary hover:underline mt-3" data-testid="actor-empty-add">
                            Ajouter le premier
                        </button>
                    )}
                </div>
            ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="actor-list">
                    {items.map(a => {
                        const editable = isAdmin || canEditEntry({ entry: a, userId: user?.id, profile, currentWorkspace });
                        const full = `${a.first_name || ''} ${a.last_name || ''}`.trim() || 'Acteur';
                        return (
                            <article
                                key={a.id}
                                className="group p-5 rounded-xl border border-border bg-white hover:border-primary/30 transition-colors flex flex-col"
                                data-testid={`actor-card-${a.id}`}
                            >
                                <div className="flex items-start justify-between gap-2 mb-3">
                                    <div className="flex-1 min-w-0">
                                        <div className="font-heading text-lg leading-tight">{full}</div>
                                        {a.role && <div className="text-xs text-secondary font-medium mt-0.5">{a.role}</div>}
                                    </div>
                                    {editable && (
                                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <Button onClick={() => openEdit(a)} size="sm" variant="ghost" className="h-7 w-7 p-0" data-testid={`actor-edit-${a.id}`}>
                                                <Pencil size={12} />
                                            </Button>
                                            <Button onClick={() => remove(a)} size="sm" variant="ghost" className="h-7 w-7 p-0" data-testid={`actor-delete-${a.id}`}>
                                                <Trash2 size={12} className="text-destructive" />
                                            </Button>
                                        </div>
                                    )}
                                </div>
                                <div className="space-y-1.5 text-xs text-foreground/80">
                                    {a.organization && (
                                        <div className="flex items-center gap-2"><Building2 size={12} className="text-muted-foreground shrink-0" /> {a.organization}</div>
                                    )}
                                    {a.email && (
                                        <div className="flex items-center gap-2"><Mail size={12} className="text-muted-foreground shrink-0" /> <a href={`mailto:${a.email}`} className="hover:underline truncate">{a.email}</a></div>
                                    )}
                                    {a.phone && (
                                        <div className="flex items-center gap-2"><Phone size={12} className="text-muted-foreground shrink-0" /> <a href={`tel:${a.phone}`} className="hover:underline">{a.phone}</a></div>
                                    )}
                                </div>
                                {a.notes && (
                                    <div className="mt-3 text-xs text-muted-foreground italic line-clamp-3">{a.notes}</div>
                                )}
                            </article>
                        );
                    })}
                </div>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-2xl">
                            {editingId ? 'Modifier l\'acteur' : 'Nouvel acteur'}
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={submit} className="space-y-3" data-testid="actor-form">
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label>Prénom</Label>
                                <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="actor-input-first_name" />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Nom</Label>
                                <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="actor-input-last_name" />
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Fonction / rôle</Label>
                            <Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="Sponsor exécutif, Chef de projet, Partenaire technique…" data-testid="actor-input-role" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Organisation</Label>
                            <Input value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} data-testid="actor-input-organization" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label>Email</Label>
                                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="actor-input-email" />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Téléphone</Label>
                                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+229 …" data-testid="actor-input-phone" />
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Notes (facultatif)</Label>
                            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="actor-input-notes" />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Annuler</Button>
                            <Button type="submit" disabled={submitting} className="bg-primary hover:bg-primary-hover" data-testid="actor-submit">
                                {submitting && <Loader2 size={14} className="mr-2 animate-spin" />}
                                Enregistrer
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
